import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";
import { communityCategories } from "@/mocks/communityData";
import { usePosts } from "@/hooks/usePosts";

const categoryMap: Record<string, string> = {
  free: "자유게시판",
  promote: "홍보센터",
  picks: "월드컵픽스터",
};

const dbCategoryMap: Record<string, string> = {
  free: "community",
  promote: "promote",
  picks: "tip",
  scam: "scam_report",
};

export default function CommunityCategoryPage() {
  const { category } = useParams<{ category: string }>();
  const [searchQuery, setSearchQuery] = useState("");

  const categoryLabel = category ? (categoryMap[category] || "전체") : "전체";
  const writeCategory = category ? categoryMap[category] || "" : "";
  const dbCategory = category ? dbCategoryMap[category] : undefined;
  const { posts, loading, error, refetch } = usePosts(category === "all" ? undefined : dbCategory);

  const filteredPosts = posts.filter((post) => {
    const matchSearch = !searchQuery || post.title.includes(searchQuery) || post.author.includes(searchQuery);
    return matchSearch;
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      {/* Page Header */}
      <section className="bg-background-100 border-b border-background-300">
        <div className="max-w-[1000px] mx-auto px-3 md:px-4 py-8 md:py-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-400 transition-colors mb-3"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </span>
            홈으로
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center text-primary-500">
                <i className="ri-chat-smile-3-line text-2xl" />
              </span>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-foreground-700">{categoryLabel}</h1>
                <p className="text-sm text-foreground-600 mt-0.5">
                  {categoryLabel === "전체" ? "모든 게시글을 확인하세요" : `${categoryLabel} 카테고리의 게시글입니다`}
                </p>
              </div>
            </div>
            <Link
              to={`/write?category=${writeCategory}`}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-pencil-line" />
              </span>
              글쓰기
            </Link>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-[1000px] mx-auto px-3 md:px-4 py-6">
        {/* Category Tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
          <div className="flex items-center gap-1 flex-wrap">
            <Link
              to="/community"
              className={`px-3 py-1.5 text-xs font-bold rounded-full transition-colors whitespace-nowrap ${
                !category || category === "all"
                  ? "bg-primary-500 text-foreground-50"
                  : "bg-background-100 text-foreground-600 hover:text-foreground-700 border border-background-300"
              }`}
            >
              전체
            </Link>
            {communityCategories.filter((c) => c.id !== "all").map((cat) => (
              <Link
                key={cat.id}
                to={`/community/${cat.id}`}
                className={`px-3 py-1.5 text-xs font-bold rounded-full transition-colors whitespace-nowrap ${
                  category === cat.id
                    ? "bg-primary-500 text-foreground-50"
                    : "bg-background-100 text-foreground-600 hover:text-foreground-700 border border-background-300"
                }`}
              >
                {cat.label}
              </Link>
            ))}
          </div>
          <div className="relative flex-1 max-w-[300px] ml-auto">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-600">
              <i className="ri-search-line text-sm" />
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="제목 또는 작성자 검색"
              className="w-full bg-background-100 border border-background-300 rounded-lg pl-9 pr-4 py-2 text-sm text-foreground-700 placeholder-foreground-500 outline-none focus:border-primary-500/50 transition-colors"
            />
          </div>
        </div>

        {/* Post List */}
        <div className="bg-background-100 rounded-lg border border-background-300 overflow-hidden">
          <div className="hidden sm:flex items-center gap-4 px-4 py-3 bg-background-200/50 border-b border-background-300 text-[11px] font-bold text-foreground-600">
            <span className="w-20">카테고리</span>
            <span className="flex-1">제목</span>
            <span className="w-20 text-center">작성자</span>
            <span className="w-16 text-center">조회</span>
            <span className="w-16 text-center">추천</span>
            <span className="w-20 text-center">날짜</span>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <span className="w-5 h-5 flex items-center justify-center animate-spin text-foreground-500">
                <i className="ri-loader-4-line text-xl" />
              </span>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-background-200 flex items-center justify-center">
                <span className="w-6 h-6 flex items-center justify-center text-red-400">
                  <i className="ri-error-warning-line" />
                </span>
              </div>
              <p className="text-sm text-red-400 mb-3">{error}</p>
              <button
                onClick={refetch}
                className="px-4 py-2 text-xs bg-primary-500 text-foreground-50 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                다시 시도
              </button>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-background-200 flex items-center justify-center">
                <span className="w-6 h-6 flex items-center justify-center text-foreground-600">
                  <i className="ri-inbox-line" />
                </span>
              </div>
              <p className="text-sm text-foreground-600">검색 결과가 없습니다</p>
            </div>
          ) : (
            filteredPosts.map((post) => (
              <Link
                key={post.id}
                to={`/post/${post.id}`}
                className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 py-3 border-b border-background-300/50 last:border-0 hover:bg-background-200/30 transition-colors cursor-pointer"
              >
                <span className="sm:w-20">
                  <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold bg-secondary-500/10 text-secondary-400 rounded">
                    {post.category}
                  </span>
                </span>
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <span className="text-sm text-foreground-700 truncate">{post.title}</span>
                  <span className="text-[10px] text-primary-500 flex-shrink-0">
                    [{post.comments}]
                  </span>
                </div>
                <span className="sm:w-20 text-xs text-foreground-600 sm:text-center">{post.author}</span>
                <span className="sm:w-16 text-xs text-foreground-600 sm:text-center">{post.views}</span>
                <span className="sm:w-16 text-xs text-foreground-600 sm:text-center">{post.likes}</span>
                <span className="sm:w-20 text-xs text-foreground-600 sm:text-center">{post.date}</span>
              </Link>
            ))
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}